import {isPlatformBrowser} from '@angular/common';
import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {LanguageService, Product} from '@spartacus/core';
import {CurrentProductService} from '@spartacus/storefront';
import {Subscription} from 'rxjs';
import {filter, pairwise, startWith} from 'rxjs/operators';
import {DcpProductUtilitiesService} from '../../product/services/dcp-product-utilities.service';
import {DcpCurrencyService} from '../pricing/dcp-currency.service';

declare const _trackData: (object: object) => void | undefined;

const EXTERNAL_CAMPAIGN: string = 'External Campaign';
const INTERNAL_CAMPAIGN: string = 'Internal Campaign';
const EXTERNAL_REFERRER: string = 'External Referrer';
const HOME_PAGE: string = 'Home Page';
const SEARCH_PAGE: string = 'Search Page';
const DIRECT_ACCESS: string = 'Direct Access';

export class PageHitData {
  events: {
    pageView: boolean;
  };
  page: {
    detail?: string;
    name?: string;
    url: string;
    referrer: string;
    section?: string;
    language?: string;
  };
  ecommerce?: {
    currency?: string;
  };
}

class ProductData {
  events: {
    productView: boolean;
  };
  ecommerce: {
    products: {
      ID: string;
      findingMethod: string;
      name: string;
      partnerName: string;
    }[];
  };
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DataTrackingService {
  navigatedSinceLastProductUpdate: boolean = true;
  lastNavigations: NavigationEnd[] = [];
  currentProductCode: string = '';
  currentCurrency: string;
  currentLanguage: string;
  private currentProductSubscription: Subscription = Subscription.EMPTY;
  // after followup this can be fully removed

  private featureIsEnabled: boolean = false;

  constructor(
    private currencyService: DcpCurrencyService,
    private languageService: LanguageService,
    private router: Router,
    private currentProductService: CurrentProductService,
    private productUtilitiesService: DcpProductUtilitiesService,
    @Inject(PLATFORM_ID) public platformId: string
  ) {
    this.currencyService
      .getActive()
      .pipe(untilDestroyed(this))
      .subscribe((currency) => {
        this.currentCurrency = currency;
      });
    this.languageService
      .getActive()
      .pipe(untilDestroyed(this))
      .subscribe((language) => {
        this.currentLanguage = language;
      });
  }

  public setupPageHitTracking() {
    if (isPlatformBrowser(this.platformId)) {
      this.router.events.pipe(untilDestroyed(this)).subscribe((e) => {
        if (e instanceof NavigationEnd) {
          setTimeout(() => {
            this.trackPageHitData();
          });
        }
      });
    }
  }

  public setupProductDataTracking() {
    if (isPlatformBrowser(this.platformId)) {
      this.router.events
        .pipe(
          startWith(new NavigationEnd(-1, '', '')),
          filter((e) => e instanceof NavigationEnd),
          pairwise(),
          untilDestroyed(this)
        )
        .subscribe((e) => {
          this.navigatedSinceLastProductUpdate = true;
          this.lastNavigations = e as unknown as NavigationEnd[];
        });
      this.currentProductSubscription.unsubscribe();
      this.currentProductSubscription = this.currentProductService
        .getProduct()
        .pipe(untilDestroyed(this))
        .subscribe((value) => {
          if (
            value &&
            value.code &&
            this.router.url?.includes('/product/') &&
            (value.code !== this.currentProductCode || this.navigatedSinceLastProductUpdate)
          ) {
            this.navigatedSinceLastProductUpdate = false;
            this.currentProductCode = value.code;
            this.trackProductData(value);
          }
        });
    }
  }

  public getFindingMethod(): string {
    if (isPlatformBrowser(this.platformId) && this.lastNavigations && this.lastNavigations.length === 2) {
      if (window.location.href.match(/[?&]campaigncode=/) || window.location.href.match(/[?&]source=/)) {
        return EXTERNAL_CAMPAIGN;
      } else if (window.location.href.match(/[?&]url_id=/)) {
        return INTERNAL_CAMPAIGN;
      } else if (
        !this.lastNavigations[0].url.length &&
        document.referrer &&
        document.referrer.length &&
        !document.referrer.startsWith(window.location.origin) &&
        this.isNonSapUrl(document.referrer)
      ) {
        return EXTERNAL_REFERRER;
      } else if (
        document.referrer &&
        document.referrer.length &&
        this.lastNavigations[0].url &&
        this.lastNavigations[0].url.length &&
        !this.lastNavigations[0].url?.includes('/search') &&
        !this.lastNavigations[0].url?.includes('/product/')
      ) {
        return HOME_PAGE;
      } else if (
        document.referrer &&
        document.referrer.length &&
        this.lastNavigations[0].url &&
        this.lastNavigations[0].url.length
      ) {
        return SEARCH_PAGE;
      } else {
        return DIRECT_ACCESS;
      }
    }
    return undefined;
  }

  protected trackPageHitData() {
    if (this.featureIsEnabled && isPlatformBrowser(this.platformId)) {
      const data: PageHitData = new PageHitData();
      data.events = {
        pageView: true,
      };
      data.ecommerce = {
        currency: this.currentCurrency,
      };
      data.page = {
        language: this.currentLanguage,
        url: window.location.href,
        referrer: document.referrer,
      };
      this.trackData(data);
    }
  }

  protected trackProductData(product: Product) {
    if (this.featureIsEnabled) {
      const data: ProductData = new ProductData();
      data.events = {
        productView: true,
      };
      data.ecommerce = {
        products: [
          {
            ID: this.productUtilitiesService.getProductId(product),
            name: product.name,
            findingMethod: this.getFindingMethod(),
            partnerName: product.vendor && product.vendor.name,
          },
        ],
      };
      this.trackData(data);
    }
  }

  protected isNonSapUrl(url: string): boolean {
    return !url.match(/[./]sap.com/);
  }

  /**
   * Explicitly sends data to adobe analytics.
   *
   * @param data to be sent
   */
  public trackData(data: any): void {
    try {
      if (isPlatformBrowser(this.platformId) && _trackData) {
        _trackData(data);
      }
    } catch (e) {}
  }
}
